import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { PLANS, money } from "@/lib/daytime";

export function BuyPassDialog({ open, initial, explorers, settings, onClose, onBought, onDayPass }) {
  const [kind, setKind] = useState(initial || "pass5");
  const [kids, setKids] = useState([]);
  const [start, setStart] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setKind(initial || "pass5"); setKids([]); setStart(""); } }, [open, initial]);
  const plans = PLANS(settings.prices).filter((p) => p.key !== "sibling");
  const max = kind === "monthly" ? 1 : kind === "family" ? 2 : 0;
  const member = kind === "monthly" || kind === "family";
  const toggle = (id) => setKids(kids.includes(id) ? kids.filter((k) => k !== id) : max === 1 ? [id] : kids.length >= max ? kids : [...kids, id]);

  const buy = async () => {
    if (kind === "day") { onDayPass(); return; }
    if (member && kids.length === 0) return toast.error("Choose which Explorer this membership is for");
    setBusy(true);
    try {
      const { data } = await api.post("/daytime/purchase", { kind, child_ids: member ? kids : [], start_date: start || null });
      onBought(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="buy-pass-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Choose your Daytime pass</DialogTitle>
          <DialogDescription>Buying a pass doesn't reserve space — you'll pick your play days next.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {plans.map((p) => (
            <button key={p.key} onClick={() => { setKind(p.key); setKids([]); }} data-testid={`buy-kind-${p.key}`}
              className={`text-left rounded-2xl border-2 p-3 ${kind === p.key ? "border-turquoise bg-turquoise/10" : "border-slate-200"}`}>
              <p className="font-display font-bold text-navy text-sm">{p.title}</p>
              <p className="font-extrabold" style={{ color: p.color }}>{money(p.price)} <span className="text-[10px] text-slate-400">{p.per || ""}</span></p>
            </button>
          ))}
        </div>
        {member && (
          <div className="mt-2">
            <p className="text-xs font-bold text-slate-500 mb-1">{kind === "family" ? "Choose up to 2 Explorers" : "Which Explorer?"}</p>
            <div className="flex flex-wrap gap-2">{explorers.map((e) => (
              <button key={e.id} onClick={() => toggle(e.id)} data-testid={`buy-child-${e.id}`} className={`px-4 py-2 rounded-full border-2 text-sm font-semibold ${kids.includes(e.id) ? "bg-navy text-white border-navy" : "border-slate-200 text-navy"}`}>{e.name}</button>
            ))}</div>
            {explorers.length === 0 && <p className="text-sm text-coral">Add an Explorer in My Explorers first.</p>}
            <label className="block text-xs font-bold text-slate-500 mt-3">Start date (optional — defaults to today)</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl mt-1" data-testid="buy-start-date" />
            <p className="text-[11px] text-slate-400 mt-1">{settings.membership_period === "calendar" ? "Memberships run to the end of the calendar month they start in." : "Memberships run 30 days from the start date."}</p>
          </div>
        )}
        {(kind === "pass5" || kind === "pass10") && <p className="text-xs text-slate-500">Visits can be used by any of your Explorers and are only used when your Explorer checks in. Valid {settings.pass_valid_days} days from purchase.</p>}
        {kind === "day" && <p className="text-xs text-slate-500">Day Passes are paid per visit — just pick your date and Explorers on the calendar. Siblings on the same day are {money(settings.prices.sibling)}.</p>}
        <Button onClick={buy} disabled={busy} className="w-full rounded-full bg-gold text-navy font-bold h-12 mt-2" data-testid="buy-pass-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : kind === "day" ? "Pick my day" : `Buy ${PLANS(settings.prices).find((p) => p.key === kind)?.title} — ${money(settings.prices[kind])}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
